import ko, { type Translations, type TranslationsBase } from './ko';
import ja from './ja';
import th from './th';
import vi from './vi';
import id from './id';
import ms from './ms';
import ptBR from './pt-br';
import my from './my';
import en from './en';
import zhTW from './zh-tw';
import zhCN from './zh-cn';
import de from './de';
import es from './es';
import fr from './fr';
import ar from './ar';

import { applyTierSeoI18n } from './tierSeoI18n';
import { applyUiLocalePatch } from './uiLocalePatch';

function withLocalePatches(base: TranslationsBase, lang: Lang): Translations {
  const withTier = applyTierSeoI18n(base as unknown as Record<string, unknown>, lang);
  return applyUiLocalePatch(withTier, lang) as unknown as Translations;
}


export type Lang = 'ko' | 'ja' | 'th' | 'vi' | 'id' | 'ms' | 'pt-BR' | 'my' | 'en' | 'zh-TW' | 'zh-CN' | 'de' | 'es' | 'fr' | 'ar';

const STABLE_LANGS: Lang[] = ['ko', 'ja', 'th', 'vi', 'id', 'zh-TW', 'zh-CN'];
const PREVIEW_LANGS: Lang[] = ['ms', 'pt-BR', 'my', 'en', 'de', 'es', 'fr', 'ar'];
// Default ON for multilingual SEO rollout. Set VITE_ENABLE_PREVIEW_LOCALES=0 to hide preview locales.
const ENABLE_PREVIEW_LOCALES = import.meta.env.VITE_ENABLE_PREVIEW_LOCALES !== '0';

export const SUPPORTED_LANGS: Lang[] = ENABLE_PREVIEW_LOCALES
  ? [...STABLE_LANGS, ...PREVIEW_LANGS]
  : STABLE_LANGS;
export const DEFAULT_LANG: Lang = 'ko';

export const translations: Record<Lang, Translations> = {
  ko: applyUiLocalePatch(ko, 'ko'),
  ja: withLocalePatches(ja, 'ja'),
  th: withLocalePatches(th, 'th'),
  vi: withLocalePatches(vi, 'vi'),
  id: withLocalePatches(id, 'id'),
  ms: withLocalePatches(ms, 'ms'),
  'pt-BR': withLocalePatches(ptBR, 'pt-BR'),
  my: withLocalePatches(my, 'my'),
  en: applyUiLocalePatch(en, 'en'),
  'zh-TW': withLocalePatches(zhTW, 'zh-TW'),
  'zh-CN': withLocalePatches(zhCN, 'zh-CN'),
  de: withLocalePatches(de, 'de'),
  es: withLocalePatches(es, 'es'),
  fr: withLocalePatches(fr, 'fr'),
  ar: withLocalePatches(ar, 'ar'),
};

export const langMeta: Record<Lang, { name: string; flag: string; hreflang: string; ogLocale: string; htmlLang: string }> = {
  ko: { name: '한국어', flag: '🇰🇷', hreflang: 'ko', ogLocale: 'ko_KR', htmlLang: 'ko' },
  ja: { name: '日本語', flag: '🇯🇵', hreflang: 'ja', ogLocale: 'ja_JP', htmlLang: 'ja' },
  th: { name: 'ไทย', flag: '🇹🇭', hreflang: 'th', ogLocale: 'th_TH', htmlLang: 'th' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳', hreflang: 'vi', ogLocale: 'vi_VN', htmlLang: 'vi' },
  id: { name: 'Indonesia', flag: '🇮🇩', hreflang: 'id', ogLocale: 'id_ID', htmlLang: 'id' },
  ms: { name: 'Bahasa Melayu', flag: '🇲🇾', hreflang: 'ms', ogLocale: 'ms_MY', htmlLang: 'ms' },
  'pt-BR': { name: 'Português (Brasil)', flag: '🇧🇷', hreflang: 'pt-BR', ogLocale: 'pt_BR', htmlLang: 'pt-BR' },
  my: { name: 'မြန်မာ', flag: '🇲🇲', hreflang: 'my', ogLocale: 'my_MM', htmlLang: 'my' },
  en: { name: 'English', flag: '🇺🇸', hreflang: 'en', ogLocale: 'en_US', htmlLang: 'en' },
  de: { name: 'Deutsch', flag: '🇩🇪', hreflang: 'de', ogLocale: 'de_DE', htmlLang: 'de' },
  es: { name: 'Español', flag: '🇪🇸', hreflang: 'es', ogLocale: 'es_ES', htmlLang: 'es' },
  fr: { name: 'Français', flag: '🇫🇷', hreflang: 'fr', ogLocale: 'fr_FR', htmlLang: 'fr' },
  ar: { name: 'العربية', flag: '🇸🇦', hreflang: 'ar', ogLocale: 'ar_SA', htmlLang: 'ar' },
  'zh-TW': { name: '繁體中文', flag: '🇹🇼', hreflang: 'zh-Hant', ogLocale: 'zh_TW', htmlLang: 'zh-Hant' },
  'zh-CN': { name: '简体中文', flag: '🇨🇳', hreflang: 'zh-Hans', ogLocale: 'zh_CN', htmlLang: 'zh-Hans' },
};

const LANG_ALIASES: Record<string, Lang> = {
  ko: 'ko',
  ja: 'ja',
  th: 'th',
  vi: 'vi',
  id: 'id',
  ms: 'ms',
  my: 'my',
  en: 'en',
  de: 'de',
  es: 'es',
  fr: 'fr',
  ar: 'ar',
  'pt-br': 'pt-BR',
  ptbr: 'pt-BR',
  'zh-tw': 'zh-TW',
  zhtw: 'zh-TW',
  'zh-cn': 'zh-CN',
  zhcn: 'zh-CN',
};

export const normalizeLangCode = (value: string | null | undefined): Lang | null => {
  if (!value) return null;
  const normalized = value.trim().replace('_', '-').toLowerCase();
  return LANG_ALIASES[normalized] || null;
};

/** Get lang prefix for URLs: '' for default (ko), '/ja' etc for others */
export const langPrefix = (lang: Lang): string => (lang === DEFAULT_LANG ? '' : `/${lang}`);

/** Parse lang from pathname */
export const parseLangFromPath = (pathname: string): Lang => {
  const seg = pathname.split('/')[1];
  const normalized = normalizeLangCode(seg);
  if (normalized && SUPPORTED_LANGS.includes(normalized) && normalized !== DEFAULT_LANG) {
    return normalized;
  }
  return DEFAULT_LANG;
};

/** Remove lang prefix from path */
export const stripLangPrefix = (pathname: string): string => {
  const seg = pathname.split('/')[1];
  const normalized = normalizeLangCode(seg);
  // `/ko` is a legacy alias for the unprefixed default locale. Strip it too
  // so runtime canonicals cannot preserve duplicate `/ko/*` paths.
  if (normalized && SUPPORTED_LANGS.includes(normalized)) {
    return pathname.replace(`/${seg}`, '') || '/';
  }
  return pathname;
};

// ===== Script detection ranges =====
const HANGUL_RE = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/;
const HIRAGANA_KATAKANA_RE = /[\u3040-\u30FF]/;
const CJK_HAN_RE = /[\u3400-\u4DBF\u4E00-\u9FFF]/; // Han characters (used by ja/zh-TW/zh-CN)
const THAI_RE = /[\u0E00-\u0E7F]/;
const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const MYANMAR_RE = /[\u1000-\u109F]/;

/**
 * Per-locale "expected script" check.
 * Returns true if `s` contains script that does NOT belong in `lang`.
 * Pure ASCII / digits / punctuation are always allowed (they're neutral).
 */
const isWrongScriptForLang = (s: string, lang: string): boolean => {
  if (!s) return false;
  const hasHangul = HANGUL_RE.test(s);
  const hasKana = HIRAGANA_KATAKANA_RE.test(s);
  const hasHan = CJK_HAN_RE.test(s);
  const hasThai = THAI_RE.test(s);
  const hasArabic = ARABIC_RE.test(s);
  const hasMyanmar = MYANMAR_RE.test(s);

  switch (lang) {
    case 'ko':
      // Korean pages can contain Han (hanja) and Latin numbers. Reject kana/Thai/Arabic/Myanmar.
      return hasKana || hasThai || hasArabic || hasMyanmar;
    case 'ja':
      // Japanese pages: kana + Han allowed. Reject Hangul/Thai/Arabic/Myanmar.
      return hasHangul || hasThai || hasArabic || hasMyanmar;
    case 'zh-TW':
    case 'zh-CN':
      // Chinese pages: Han only. Reject Hangul/kana/Thai/Arabic/Myanmar.
      return hasHangul || hasKana || hasThai || hasArabic || hasMyanmar;
    case 'th':
      return hasHangul || hasKana || hasHan || hasArabic || hasMyanmar;
    case 'my':
      return hasHangul || hasKana || hasHan || hasThai || hasArabic;
    case 'ar':
      // Arabic pages: only Arabic + Latin tokens (numbers/brand names). Reject all CJK/Thai/Hangul/Myanmar.
      return hasHangul || hasKana || hasHan || hasThai || hasMyanmar;
    // Latin-script locales — reject ANY non-Latin script.
    case 'en':
    case 'vi':
    case 'id':
    case 'ms':
    case 'pt-BR':
    case 'de':
    case 'es':
    case 'fr':
      return hasHangul || hasKana || hasHan || hasThai || hasArabic || hasMyanmar;
    default:
      return false;
  }
};

/**
 * Strict per-locale fallback chain. The chain is designed so that NO locale ever
 * receives content in a foreign script — English is always the universal safe fallback.
 *
 * Rule: try the requested lang → en → another locale whose script is compatible
 *       (e.g. ja can borrow from zh-* because they share Han, but not from ko).
 */
const FALLBACK_CHAIN: Record<string, string[]> = {
  ko:      ['ko', 'en'],
  ja:      ['ja', 'en', 'zh-TW', 'zh-CN'],
  'zh-TW': ['zh-TW', 'zh-CN', 'en'],
  'zh-CN': ['zh-CN', 'zh-TW', 'en'],
  th:      ['th', 'en'],
  vi:      ['vi', 'en'],
  id:      ['id', 'ms', 'en'],
  ms:      ['ms', 'id', 'en'],
  my:      ['my', 'en'],
  'pt-BR': ['pt-BR', 'es', 'en'],
  de:      ['de', 'en'],
  es:      ['es', 'pt-BR', 'en'],
  fr:      ['fr', 'en'],
  ar:      ['ar', 'en'],
  en:      ['en'],
};

export const pickByLang = <T>(
  table: Record<string, T>,
  lang: string,
  _fallback: Lang = DEFAULT_LANG,
): T => {
  const chain = FALLBACK_CHAIN[lang] ?? [lang, 'en'];

  // 1) Walk the per-locale fallback chain; for string values enforce script correctness.
  for (const code of chain) {
    const v = table[code];
    if (v === undefined || v === null) continue;
    if (typeof v !== 'string') return v;
    if (v.trim() === '') continue;
    if (!isWrongScriptForLang(v, lang)) return v as T;
  }

  // 2) Last resort: any non-empty string value from the table that is script-safe for this lang.
  for (const key of Object.keys(table)) {
    const v = table[key];
    if (typeof v === 'string' && v.trim() !== '' && !isWrongScriptForLang(v, lang)) {
      return v as T;
    }
  }

  // 3) Absolute fallback (caller may handle empty case).
  return (table[lang] ?? table.en ?? (Object.values(table)[0] as T));
};

/** Exported for diagnostics / verification scripts. */
export const __i18nDiagnostics = { isWrongScriptForLang };

export type { Translations };
